
import React, { useState, useMemo } from 'react';
import { GLOSSARY_DATA, type GlossaryTerm } from '../data/glossary';
import { MagnifyingGlassIcon, XMarkIcon, ChevronRightIcon, AcademicCapIcon, MapIcon, ShieldCheckIcon, PresentationChartLineIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface GlossaryDrawerProps {
    isOpen: boolean;
    initialSearch?: string | null;
    onClose: () => void;
}

const CategoryIcon: React.FC<{ category: string }> = ({ category }) => {
    switch (category) {
        case 'Geopolitical': return <MapIcon className="h-4 w-4 text-amber-500" />;
        case 'Security': return <ShieldCheckIcon className="h-4 w-4 text-emerald-500" />;
        case 'Technical': return <AcademicCapIcon className="h-4 w-4 text-blue-500" />;
        case 'Strategic': return <PresentationChartLineIcon className="h-4 w-4 text-indigo-500" />;
        default: return null;
    }
};

const GlossaryDrawer: React.FC<GlossaryDrawerProps> = ({ isOpen, initialSearch, onClose }) => {
    const [searchTerm, setSearchTerm] = React.useState('');

    React.useEffect(() => {
        if (isOpen && initialSearch) {
            setSearchTerm(initialSearch);
        } else if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen, initialSearch]);

    const filteredTerms = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return GLOSSARY_DATA;
        return GLOSSARY_DATA.filter(t => 
            t.term.toLowerCase().includes(term) || 
            t.acronym?.toLowerCase().includes(term) ||
            t.definition.toLowerCase().includes(term)
        );
    }, [searchTerm]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] overflow-hidden select-none">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity cursor-pointer"
                onClick={onClose}
            ></div>

            {/* Sidebar drawer */}
            <div className="absolute inset-y-0 right-0 max-w-full flex">
                <div className="relative w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white shadow-2xl border-l border-slate-200">
                        {/* Header */}
                        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-slate-900">Intelligence Index</h2>
                                <p className="text-xs text-slate-500 font-medium">Standardized Terminology & Frameworks</p>
                            </div>
                            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200 transition-colors">
                                <XMarkIcon className="h-6 w-6 text-slate-500" />
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="p-4 border-b border-slate-100">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search terminology..."
                                    className="w-full pl-10 pr-4 py-2 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-800 placeholder-slate-400 text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Scrollable List */}
                        <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-slate-200">
                            {filteredTerms.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="bg-slate-50 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                        <MagnifyingGlassIcon className="h-6 w-6 text-slate-300" />
                                    </div>
                                    <p className="text-slate-500 text-sm">No terms found matching "{searchTerm}"</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {filteredTerms.map((item, idx) => (
                                        <div key={idx} className="group">
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <CategoryIcon category={item.category} />
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                    {item.category}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center group-hover:text-blue-600 transition-colors">
                                                {item.term}
                                                {item.acronym && (
                                                    <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
                                                        {item.acronym}
                                                    </span>
                                                )}
                                            </h3>
                                            <p className="text-sm text-slate-600 leading-relaxed mt-1">
                                                {item.definition}
                                            </p>
                                            {item.externalUrl && (
                                                <a 
                                                    href={item.externalUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline transition-all"
                                                >
                                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                    Research Deep Dive
                                                </a>
                                            )}
                                            {idx < filteredTerms.length - 1 && (
                                                <div className="mt-6 border-b border-slate-50 shadow-sm" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer info */}
                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                            <p className="text-[10px] text-slate-400 flex items-center gap-2">
                                <ShieldCheckIcon className="h-3 w-3" />
                                Verifying risk against international security standards.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlossaryDrawer;
